import { useState, useEffect, useRef } from "react";
import { Button, Icon, notification, Switch, Select, Spin } from "antd";
import QRCode from "qrcode.react";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import PDFDocument from "@react-pdf/pdfkit";
import SVGtoPDF from "svg-to-pdfkit";
import BlobStream from "blob-stream";
import StickerModal from "../StickerModal";
import "./styles.css";
import { useFreire } from "../../utils/freireContext";
import * as texts from "./locales";
import { useWhitelabels } from "../WhitelabelThemesProvider";
import { sorter } from "../../utils/sorter";

const { Option } = Select;
const ButtonGroup = Button.Group;

function QrCode({ station = {}, size = 424, isTupi = false }) {
  const [hasLogo, setHasLogo] = useState(true);
  const [visibleSticker, setVisibleSticker] = useState(false);
  const [qrcodeSticker, setQrcodeSticker] = useState("");
  const containerRef = useRef(null);
  const { freire } = useFreire();
  const {
    selectedWhitelabelTheme,
    allWhitelabels,
    setSelectedWhitelabelByAlias,
  } = useWhitelabels();
  const [logoImage, setLogoImage] = useState(null);
  const [isLogoLoading, setIsLogoLoading] = useState(false);

  // Download and convert logo to base64 when component mounts or logo URL changes
  useEffect(() => {
    if (selectedWhitelabelTheme?.qrCode?.logo) {
      setIsLogoLoading(true);
      fetch(selectedWhitelabelTheme.qrCode.logo)
        .then((response) => response.blob())
        .then((blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoImage(reader.result);
          };
          reader.readAsDataURL(blob);
        })
        .catch((error) => {
          console.error("Error downloading logo:", error);
          setLogoImage(null);
        })
        .finally(() => {
          setIsLogoLoading(false);
        });
    } else {
      setLogoImage(null);
    }
  }, [selectedWhitelabelTheme?.qrCode?.logo]);

  const stationCode = station.code || station.stationCode || station.stationID;
  const codeLabelText = freire(texts.CODE_LABEL);
  let stationCodeLabel = "";
  if (stationCode) {
    const labelPrefix = codeLabelText ? `${codeLabelText}: ` : "";
    stationCodeLabel = `${labelPrefix}${stationCode}`;
  }

  const buildSvgWithCode = () => {
    const container = containerRef.current;
    if (!container) return null;

    const svg = container.querySelector("svg");
    if (!svg) return null;

    const clonedSvg = svg.cloneNode(true);
    const namespace = "http://www.w3.org/2000/svg";

    const widthAttr = parseFloat(clonedSvg.getAttribute("width")) || size;
    const heightAttr = parseFloat(clonedSvg.getAttribute("height")) || size;

    const viewBoxAttr = clonedSvg.getAttribute("viewBox");
    const [minX, minY, boxWidth, boxHeight] = viewBoxAttr
      ? viewBoxAttr.split(" ").map((value, index) => {
          if (index < 2) return Number(value) || 0;
          return Number(value) || size;
        })
      : [0, 0, widthAttr, heightAttr];

    const computedWidth = boxWidth > 0 ? boxWidth : widthAttr;
    const computedHeight = boxHeight > 0 ? boxHeight : heightAttr;

    const padding = computedWidth * 0.08;
    const fontSize = computedWidth * 0.08;
    const totalHeight = computedHeight + padding + fontSize;

    clonedSvg.setAttribute("width", widthAttr);
    clonedSvg.setAttribute("height", heightAttr + padding + fontSize);
    clonedSvg.setAttribute("viewBox", `${minX} ${minY} ${computedWidth} ${totalHeight}`);
    clonedSvg.setAttribute("xmlns", namespace);
    clonedSvg.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

    const backgroundRect = document.createElementNS(namespace, "rect");
    backgroundRect.setAttribute("width", computedWidth);
    backgroundRect.setAttribute("height", totalHeight);
    backgroundRect.setAttribute("fill", "#ffffff");

    const firstDrawableChild = Array.from(clonedSvg.childNodes).find(
      (node) => node.nodeType === 1 && node.nodeName !== "defs"
    );

    if (firstDrawableChild) {
      clonedSvg.insertBefore(backgroundRect, firstDrawableChild);
    } else {
      clonedSvg.appendChild(backgroundRect);
    }

    if (stationCodeLabel) {
      const textElement = document.createElementNS(namespace, "text");
      textElement.textContent = stationCodeLabel;
      textElement.setAttribute("x", computedWidth / 2);
      textElement.setAttribute(
        "y",
        computedHeight + padding + fontSize / 2
      );
      textElement.setAttribute("font-size", fontSize);
      textElement.setAttribute(
        "font-family",
        "'Gabarito', 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif"
      );
      textElement.setAttribute("font-weight", "600");
      textElement.setAttribute("fill", "#000000");
      textElement.setAttribute("text-anchor", "middle");
      textElement.setAttribute("dominant-baseline", "middle");
      clonedSvg.appendChild(textElement);
    }

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);

    return {
      svgString,
      width: Math.ceil(widthAttr),
      height: Math.ceil(computedHeight + padding + fontSize),
    };
  };

  const downloadPNG = () => {
    const container = containerRef.current;
    if (!container) return;

    const svg = container.querySelector("svg");
    if (!svg) return;

    const imageElement = svg.querySelector("image");
    const originalXmlns = svg.getAttribute("xmlns");
    const originalXlink = imageElement?.getAttribute("xmlns:xlink");

    svg.removeAttribute("xmlns");
    if (imageElement) {
      imageElement.removeAttribute("xmlns:xlink");
    }

    const renderCanvas = () =>
      html2canvas(container, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
      })
        .then((canvas) => {
          const pngUrl = canvas
            .toDataURL("image/png")
            .replace("image/png", "image/octet-stream");

          const downloadLink = document.createElement("a");
          downloadLink.href = pngUrl;
          downloadLink.download = `${station.name} (${station.stationID}) - QrCode.png`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        })
        .catch(() => {
          notification.error({
            message: "Ops...",
            description: freire(texts.ERROR_QRCODE),
          });
        })
        .finally(() => {
          if (originalXmlns) {
            svg.setAttribute("xmlns", originalXmlns);
          }
          if (imageElement && originalXlink) {
            imageElement.setAttribute("xmlns:xlink", originalXlink);
          }
        });

    const fontsReady = document.fonts?.ready;
    const waitForFonts =
      typeof fontsReady?.then === "function"
        ? fontsReady.catch(() => undefined)
        : Promise.resolve();

    waitForFonts.then(renderCanvas);
  };

  const downloadSVG = () => {
    const svgWithCode = buildSvgWithCode();
    if (!svgWithCode) return;

    const { svgString } = svgWithCode;

    const file = new File(
      [svgString],
      `${station.name} (${station.stationID}) - QrCode.svg`,
      {
        type: "image/svg+xml;charset=utf-8",
      }
    );
    saveAs(file);
  };

  const downloadPDF = () => {
    try {
      const stickerSvg = document.getElementById("sticker");

      const viewBox = stickerSvg.getAttribute("viewBox").split(" ");
      const withBorder = stickerSvg.getAttribute("type") === "border";
      const width = Number(viewBox[2]);
      const height = Number(viewBox[3]);

      const doc = new PDFDocument({
        size: [width + 40, height + 40],
      });

      const space = withBorder ? 20 : 0;
      SVGtoPDF(doc, stickerSvg.outerHTML, space, space, {
        assumePt: true,
      });

      const stream = doc.pipe(BlobStream());

      stream.on("finish", () => {
        let blob = stream.toBlob("application/pdf");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${station.name} (${station.stationID}) - adesivo completo.pdf`;
        link.click();
      });

      doc.end();
    } catch (error) {
      console.log("ERROR downloadStickerPDF ->", error.message);
      notification.error({
        message: "Ops...",
        description: freire(texts.ERROR_SAVE_STICKER),
      });
    }
  };

  const getQrCodeSvg = () => {
    if (!containerRef.current) return null;

    const qrcode = containerRef.current.querySelector("svg");
    if (!qrcode) return null;

    const cloned = qrcode.cloneNode(true);
    const logo = cloned.getElementsByTagName("image")[0];
    cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    if (logo) {
      logo.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    }

    return cloned;
  };

  const showStickerModal = () => {
    let qrcode = getQrCodeSvg();
    setQrcodeSticker(qrcode);
    setVisibleSticker(true);
  };

  if (!selectedWhitelabelTheme?.qrCode)
    return (
      <div className="containerQrCode">
        <span
          style={{
            fontSize: 16,
            fontWeight: 500,
          }}
        >
          {freire(texts.ERROR_QRCODE)}
        </span>
      </div>
    );

  const { size_logo, url_prefix } = selectedWhitelabelTheme.qrCode;

  return (
    <div className="containerQrCode">
      <StickerModal
        visible={visibleSticker}
        station={station}
        qrcode={qrcodeSticker}
        onClose={() => setVisibleSticker(false)}
        onDownload={() => downloadPDF()}
      />

      <div
        style={{
          marginBottom: 25,
          display: "flex",
          alignItems: "center",
          gap: "32px",
        }}
      >
        {isTupi && (
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            value={selectedWhitelabelTheme.alias}
            onChange={(newAlias) => setSelectedWhitelabelByAlias(newAlias)}
          >
            {sorter(Object.values(allWhitelabels)).map((whitelabel) => {
              if (!whitelabel?.qrCode) return null;
              return (
                <Option
                  key={whitelabel.alias}
                  value={whitelabel.alias}
                  label={whitelabel.name}
                >
                  {whitelabel.name}
                </Option>
              );
            })}
          </Select>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch
            checkedChildren={<Icon type="check" />}
            unCheckedChildren={<Icon type="close" />}
            defaultChecked={hasLogo}
            onChange={(checked) => {
              setHasLogo(checked);
            }}
          />
          <span className="textSwitch">{freire(texts.LOGO)}</span>
        </div>
      </div>
      <div
        ref={containerRef}
        className="qrcode_capture"
        style={{ position: "relative", width: size }}
      >
        <div
          className="qrcode_capture__canvas"
          style={{ width: size, height: size }}
        >
          {isLogoLoading && (
            <div className="qrcode_capture__loading">
              <Spin size="large" />
            </div>
          )}
          <QRCode
            size={size}
            level={"H"}
            renderAs="svg"
            value={`${url_prefix}/${station.stationID}`}
            imageSettings={{
              src: !isLogoLoading && hasLogo ? logoImage || "" : "",
              excavate: hasLogo,
              ...size_logo,
            }}
          />
        </div>
        {stationCodeLabel && (
          <div className="qrcode_capture__code">{stationCodeLabel}</div>
        )}
      </div>

      <div style={{ marginTop: 25, alignItems: "center" }}>
        <span
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: "#1890ff",
            marginRight: 15,
          }}
        >
          {freire(texts.DOWNLOAD)}:
        </span>
        <ButtonGroup>
          <Button onClick={downloadPNG} type="primary">
            PNG
          </Button>
          <Button onClick={downloadSVG} type="primary">
            SVG
          </Button>
          {selectedWhitelabelTheme.full_sticker && (
            <Button onClick={() => showStickerModal()} type="primary">
              {freire(texts.STICKER)}
            </Button>
          )}
        </ButtonGroup>
      </div>
    </div>
  );
}

export default QrCode;
